<section
  class="pe-card"
  [attr.aria-busy]="state().loading || releasesState() === 'loading' || null"
  [attr.aria-label]="'testResults.plaene.label' | translate"
>
  <div class="pe-head">
    <div data-col="chevron"></div>
    <div data-col="plan">{{ 'testResults.plaene.head.plan' | translate }}</div>
    <div data-col="titel">{{ 'testResults.plaene.head.titel' | translate }}</div>
    <div data-col="fortschritt">{{ 'testResults.plaene.head.fortschritt' | translate }}</div>
    <div data-col="passrate">{{ 'testResults.plaene.head.passRate' | translate }}</div>
    <div data-col="fehler">{{ 'testResults.plaene.head.fehler' | translate }}</div>
    <div data-col="freigabe">{{ 'testResults.plaene.head.freigabe' | translate }}</div>
  </div>

  @if (!releaseId() && releasesState() === 'loading') {
    <p class="pe-state" role="status" data-testid="plan-loading">{{ 'testResults.plaene.loading' | translate }}</p>
  } @else if (!releaseId() && releasesState() === 'error') {
    <p class="pe-state pe-state--error" role="alert" data-testid="plan-releases-error">
      {{ 'testResults.releasesLoadError' | translate }}
    </p>
  } @else if (!releaseId()) {
    <p class="pe-state" data-testid="plan-no-release">{{ 'testResults.plaene.noRelease' | translate }}</p>
  } @else if (state().error) {
    <p class="pe-state pe-state--error" role="alert" data-testid="plan-error">
      {{ 'testResults.plaene.loadError' | translate }}
    </p>
  } @else if (state().loading) {
    <p class="pe-state" role="status" data-testid="plan-loading">{{ 'testResults.plaene.loading' | translate }}</p>
  } @else if (rows().length === 0) {
    <div class="pe-empty" data-testid="plan-empty">{{ 'testResults.plaene.empty' | translate }}</div>
  } @else {
    @for (r of rows(); track r.plan.id) {
      @let open = isOpen(r.plan.id);
      <div class="pe-plan">
        <div
          class="pe-row"
          [class.pe-row--open]="open"
          role="button"
          tabindex="0"
          data-testid="plan-row"
          [attr.aria-expanded]="open"
          [attr.aria-controls]="open ? 'pe-detail-' + r.plan.id : null"
          (click)="toggle(r.plan.id)"
          (keydown)="onRowKeydown($event, r.plan.id)"
        >
          <div data-col="chevron">
            <mat-icon class="pe-row__chevron" aria-hidden="true">chevron_right</mat-icon>
          </div>
          <div data-col="plan">
            @if (r.plan.key) {
              <code class="pe-plan-chip">{{ r.plan.key }}</code>
            }
          </div>
          <div data-col="titel">
            <div class="pe-row__title">{{ r.plan.titel }}</div>
            <div class="pe-row__sub">
              {{ 'testResults.plaene.release' | translate: { release: r.plan.release } }} ·
              {{
                (r.plan.laeufeCount === 1 ? 'testResults.plaene.laeufeOne' : 'testResults.plaene.laeufeMany')
                  | translate: { count: r.plan.laeufeCount }
              }}
              @if (r.plan.verantwortlich) {
                · {{ r.plan.verantwortlich }}
              }
            </div>
          </div>
          <div data-col="fortschritt">
            <app-stack-bar
              [passed]="r.plan.passed"
              [failed]="r.plan.failed"
              [blocked]="r.plan.blocked"
              [offen]="r.plan.offen"
              [height]="8"
            />
            <div class="pe-progress__label">
              {{ 'testResults.plaene.executedOf' | translate: { executed: r.ausgefuehrt, total: r.plan.gesamt } }}
            </div>
          </div>
          @if (r.passRate === null) {
            <div data-col="passrate" data-tone="bad">—</div>
          } @else {
            <div data-col="passrate" [attr.data-tone]="passRateTone(r.passRate)">{{ r.passRate }}%</div>
          }
          <div data-col="fehler" [attr.data-tone]="r.fehler ? 'bad' : 'none'">{{ r.fehler || '—' }}</div>
          <div data-col="freigabe"><app-ampel-badge size="sm" [state]="r.ampel" /></div>
        </div>

        @if (open) {
          <div class="pe-detail" [id]="'pe-detail-' + r.plan.id">
            <div class="pe-detail__actions">
              <a
                class="pe-btn pe-btn--secondary"
                data-testid="plan-open"
                [routerLink]="['/projects', projectId(), 'test-plans', r.plan.id, 'edit']"
              >
                <mat-icon aria-hidden="true">shield</mat-icon>
                <span class="pe-btn__label">{{ 'testResults.plaene.open' | translate }}</span>
              </a>
              <button
                type="button"
                class="pe-btn pe-btn--primary"
                data-testid="plan-laeufe"
                (click)="laeufeOeffnen.emit(r.plan.key)"
              >
                <mat-icon aria-hidden="true">bar_chart</mat-icon>
                <span class="pe-btn__label">
                  {{ 'testResults.plaene.laeufe' | translate: { count: r.plan.laeufeCount } }}
                </span>
              </button>
              <span class="pe-detail__hint">{{ 'testResults.plaene.casesHint' | translate }}</span>
            </div>
            <div class="pe-detail__list">
              @for (f of r.plan.faelle; track f.testCaseId) {
                @let result = caseResult(f.status);
                <ng-template #caseContent>
                  @if (f.testCaseKey) {
                    <code class="pe-case__id">{{ f.testCaseKey }}</code>
                  }
                  <span class="pe-case__title">{{ f.titel }}</span>
                  @if (f.bugKey) {
                    <span class="pe-case__bug" [attr.title]="'testResults.plaene.bugTitle' | translate">
                      {{ f.bugKey }}
                    </span>
                  }
                  <span class="pe-case__lauf">{{ f.laufKey || '—' }}</span>
                  <app-res-pill size="sm" [status]="result.pill" [label]="result.labelKey | translate" />
                </ng-template>
                <div class="pe-case-line">
                  @if (executionsLink(f); as link) {
                    <a
                      class="pe-case pe-case--link"
                      data-testid="plan-case-row"
                      [routerLink]="link.commands"
                      [queryParams]="link.queryParams"
                    >
                      <ng-container [ngTemplateOutlet]="caseContent" />
                    </a>
                  } @else {
                    <div class="pe-case" data-testid="plan-case-row">
                      <ng-container [ngTemplateOutlet]="caseContent" />
                    </div>
                  }
                  @if (f.defects.length > 0) {
                    <app-defect-ref-chips
                      class="pe-case-line__defects"
                      [projectId]="projectId()"
                      [defects]="f.defects"
                    />
                  }
                </div>
              } @empty {
                <div class="pe-case pe-case--empty">{{ 'testResults.plaene.noCases' | translate }}</div>
              }
            </div>
          </div>
        }
      </div>
    }
  }
</section>
